<script setup lang="ts">
import { MessageSquareText, Minus, Plus, Tags, X } from 'lucide-vue-next'
import { computed, ref, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { type Modifier, RenderType } from '@/types'
import PromptChip from '@/components/search/PromptChip.vue'
import { useGeneratorStore } from '@/stores/GeneratorStore'
import { type TagInjectionMode, useTagInjection } from '@/composables/useTagInjection'

interface Props {
	modifier: Modifier
	mode?: TagInjectionMode
	showWeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	mode: 'generator' as TagInjectionMode,
	showWeight: true,
})

const emit = defineEmits<{
	(e: 'update:weight', value: number): void
	(e: 'remove'): void
}>()

const { t, te } = useI18n()

const { injectTag, isInjected, getInjectedCount } = useTagInjection(props.mode)

const weight = ref<number>(props.modifier.weight)
const showMore = shallowRef<boolean>(false)

watch(() => props.modifier, () => {
	weight.value = props.modifier.weight
})

function updateWeight(): void {
	let value = weight.value

	if (Number.isNaN(value))
		value = 0
	if (value < 0)
		value = 0
	if (value > 100)
		value = 100

	emit('update:weight', value)
}

const genStore = useGeneratorStore()
const { modifiers } = storeToRefs(genStore)

// Use the composable's getInjectedCount method instead of the local logic
const injectedCount = computed((): number =>
	Array.isArray(props.modifier.tags)
		? getInjectedCount(props.modifier.tags)
		: 0,
)

const hasDescriptionOrTags = computed((): boolean =>
	Boolean(props.modifier.tags?.length) || te(`boosters.${props.modifier.lib}.description`),
)

watch(() => weight.value, () => {
	updateWeight()
})

function validateInput(event: Event): void {
	const target = event.target as HTMLInputElement
	const val = Number.parseInt(target.value)
	if (Number.isNaN(val) || val < 0) {
		weight.value = 0
	}
	else if (val > 100) {
		weight.value = 100
	}
	else {
		weight.value = val
	}
}

function formatValue(): void {
	if (weight.value === null || weight.value === undefined || Number.isNaN(weight.value)) {
		weight.value = 0
	}
}

function getCategory(modifier: Modifier): string {
	const category = modifiers.value.find((cat) => {
		if (cat.modifiers.some(({ lib }) => lib === modifier.lib)) {
			return true
		}
		return false
	})?.category ?? ''

	return t(`boosters.categories.${category}`)
}

function open(): void {
	if (hasDescriptionOrTags.value && props.showWeight) {
		showMore.value = !showMore.value
	}
}

// Type-safe tag injection handler
function handleTagInjection(tag: string): void {
	injectTag(tag)
}
</script>

<template>
	<div
		class="flex flex-col bg-slate-800/70 border-[1px] h-full w-full border-solid border-slate-700/60 rounded-xl transition-colors duration-200 relative"
		:class="{
			'hover:bg-slate-700/70': showWeight,
		}"
	>
		<div
			v-if="injectedCount > 0"
			class="bg-purple-600/40 pointer-events-none px-2 py-1 rounded-b-xl font-bold text-purple-200 flex-center text-xsp absolute top-0 left-1/2 -translate-x-1/2"
		>
			{{ injectedCount }} {{ t('tags.label') }}
		</div>
		<div
			class="flex items-center justify-between w-full pr-1 pl-2 py-2"
			:class="{
				'cursor-pointer': hasDescriptionOrTags && props.showWeight,
			}"
			@click="open"
		>
			<div
				class="flex items-center justify-center gap-3"
			>
				<div class="relative gap-2 flex-center">
					<div
						v-if="showWeight"
						class="flex-center hover:text-red-500 text-slate-400 duration-300 cursor-pointer  h-full"
						aria-label="Delete modifier"
						@click.stop="$emit('remove')"
					>
						<X class="lucide-button-icon-sm" />
					</div>
					<img
						width="40"
						class="rounded-lg object-cover border-[1px] border-solid border-slate-600/50"
						:src="genStore.getModifierPreview(modifier, mode)"
					>
				</div>
				<div class="flex flex-col select-none">
					<div class="flex items-center gap-2">
						<span class="text-slate-200 font-medium sm:text-base text-sm">
							{{ getCategory(modifier) }}
						</span>
						<MessageSquareText
							v-if="te(`boosters.${modifier.lib}.description`)"
							v-tooltip.top="{ value: t('boosters.sug_tags'), showDelay: 500, hideDelay: 300 }"
							class="lucide-button-icon text-slate-400"
						/>
						<Tags
							v-if="modifier.tags?.length"
							v-tooltip.top="{ value: t('boosters.sug_tags'), showDelay: 500, hideDelay: 300 }"
							class="lucide-button-icon text-slate-400"
						/>
					</div>

					<span class="text-violet-300 sm:text-sm text-xsp">{{ t(`boosters.${modifier.lib}.title`) }}</span>
				</div>
			</div>

			<div
				v-if="showWeight"
				class="flex gap-1 items-center w-[50px] md:w-fit pointer-events-auto cursor-auto"
				:class="{
					'flex-col 2xl:flex-row ': mode === 'tweak',
					'flex-col md:flex-row': mode === 'generator',
				}"
				@click.stop
			>
				<Button
					:disabled="weight <= 0"
					class="button-light-purple w-full"
					@click="weight--"
				>
					<Minus class="w-3 h-3" />
				</Button>

				<div class="relative">
					<input
						v-model.number="weight"
						type="number"
						min="0"
						max="100"
						class="text-base !w-[50px] text-center bg-slate-950/30 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none border-0 outline-none"
						@input="validateInput"
						@blur="formatValue"
					>
					<span class="absolute right-0.5 top-1/2 transform -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
				</div>

				<Button
					:disabled="weight >= 100"
					class="button-light-purple w-full"
					@click="weight++"
				>
					<Plus class="w-3 h-3" />
				</Button>
			</div>
		</div>

		<div
			v-show="showMore"
			class="flex flex-col gap-3 p-4 border-0 border-t-[1px] border-solid border-slate-700/60 bg-slate-950/10"
		>
			<div
				v-if="te(`boosters.${modifier.lib}.description`)"
				class="text-sm text-slate-200 mb-2"
			>
				{{ t(`boosters.${modifier.lib}.description`) }}
			</div>

			<div
				v-if="modifier.tags"
				class="flex flex-col gap-1"
			>
				<div class="flex flex-wrap gap-1 w-full">
					<PromptChip
						v-for="(tag, index) in modifier.tags"
						:key="index"
						:word="tag"
						:emit-mode="true"
						:class="{ '!bg-purple-600': isInjected(tag) }"
						@tag="handleTagInjection"
					/>
				</div>
			</div>
		</div>
		<div
			v-if="modifier.is_new"
			v-tooltip="t('common.new')"
			:class="{
				'!top-3 2xl:!top-2 2xl:!right-2 !right-16': mode === 'tweak',
			}"
			class="w-2 h-2 bg-purple-400 rounded-full absolute top-3 md:top-2 md:right-2 right-16 cursor-pointer duration-300 hover:scale-[2]"
		/>
	</div>
</template>
